import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import { PlanAlignmentEditorProps } from "./types";

export const PlanAlignmentEditor = ({
  setAlignmentValues,
  alignmentValues,
  handleUpdatePlanAlignment,
  handleCancelEditingAlignment,
  isUpdatingAlignment,
}: PlanAlignmentEditorProps) => {
  const { t } = useTranslation();

  const handleAlignmentValueChange = useCallback(
    (field: string, value: string) => {
      setAlignmentValues({
        ...alignmentValues,
        [field]: parseFloat(value) || 0,
      });
    },
    [alignmentValues, setAlignmentValues]
  );

  const handleAlignmentIncrement = useCallback(
    (field: keyof typeof alignmentValues, step: number) => {
      const decimalPlaces =
        step < 1 ? (step.toString().split(".")[1] || "").length : 0;

      setAlignmentValues({
        ...alignmentValues,
        [field]:
          decimalPlaces > 0
            ? parseFloat((alignmentValues[field] + step).toFixed(decimalPlaces))
            : alignmentValues[field] + step,
      });
    },
    [alignmentValues, setAlignmentValues]
  );

  const handleAlignmentDecrement = useCallback(
    (field: keyof typeof alignmentValues, step: number) => {
      const decimalPlaces =
        step < 1 ? (step.toString().split(".")[1] || "").length : 0;
      setAlignmentValues({
        ...alignmentValues,
        [field]:
          decimalPlaces > 0
            ? parseFloat((alignmentValues[field] - step).toFixed(decimalPlaces))
            : alignmentValues[field] - step,
      });
    },
    [alignmentValues, setAlignmentValues]
  );

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] bg-white p-4 rounded-lg shadow-lg border">
      <div className="text-sm font-semibold mb-3 text-gray-700">
        Plan Alignment
      </div>
      <div className="space-y-3 min-w-[200px]">
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            {t("doc_viewer.offset_x")}
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleAlignmentDecrement("offsetX", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              −
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={alignmentValues.offsetX}
              onChange={(e) =>
                handleAlignmentValueChange("offsetX", e.target.value)
              }
              className="flex-1 px-2 py-1 border rounded text-sm text-center"
            />
            <button
              type="button"
              onClick={() => handleAlignmentIncrement("offsetX", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              +
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            {t("doc_viewer.offset_y")}
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleAlignmentDecrement("offsetY", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              −
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={alignmentValues.offsetY}
              onChange={(e) =>
                handleAlignmentValueChange("offsetY", e.target.value)
              }
              className="flex-1 px-2 py-1 border rounded text-sm text-center"
            />
            <button
              type="button"
              onClick={() => handleAlignmentIncrement("offsetY", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              +
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            {t("doc_viewer.plan_scale")}
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleAlignmentDecrement("scale", 0.1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              −
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={alignmentValues.scale}
              onChange={(e) =>
                handleAlignmentValueChange("scale", e.target.value)
              }
              className="flex-1 px-2 py-1 border rounded text-sm text-center"
            />
            <button
              type="button"
              onClick={() => handleAlignmentIncrement("scale", 0.1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              +
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            {t("doc_viewer.rotation_degrees")}
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleAlignmentDecrement("rotation", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              −
            </button>
            <input
              type="text"
              inputMode="numeric"
              value={alignmentValues.rotation}
              onChange={(e) =>
                handleAlignmentValueChange("rotation", e.target.value)
              }
              className="flex-1 px-2 py-1 border rounded text-sm text-center"
            />
            <button
              type="button"
              onClick={() => handleAlignmentIncrement("rotation", 1)}
              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
            >
              +
            </button>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleUpdatePlanAlignment}
            disabled={isUpdatingAlignment}
            className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
          >
            {isUpdatingAlignment ? (
              <div className="flex items-center justify-center">
                <Loader className="w-3 h-3 animate-spin mr-1" />
                {t("doc_viewer.updating")}
              </div>
            ) : (
              t("doc_viewer.update")
            )}
          </button>
          <button
            onClick={handleCancelEditingAlignment}
            disabled={isUpdatingAlignment}
            className="flex-1 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 text-sm"
          >
            {t("doc_viewer.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};
